import { getUsers } from './users';
import { getRealUserCount } from './firebase';
import { isWithinTimeframe, getDateFromTimestamp } from '../../utils/dates';

// Format ratio with percentage formatting option
const formatRatio = (ratio, percentage = true) => {
  if (isNaN(ratio) || ratio === null || ratio === undefined) {
    return 0;
  }
  // Return the percentage as a number (not string) so safeFixed can format it
  return percentage ? (ratio * 100) : ratio;
};

// Calculate the sum of purchases made in the last 24 hours
const purchasesLast24HoursAmount = (usersData) => {
  const now = Date.now();

  const prices = usersData?.flatMap(user =>
    (user.shop?.activity?.itemsAcquired || [])
      .filter(item => {
        const createdAtDate = getDateFromTimestamp(item.createdAt);
        return (now - createdAtDate.getTime()) <= 24 * 60 * 60 * 1000;
      })
      .map(item => item.price)
  ).filter(price => price !== undefined);

  const convertedPrices = convertPrices(prices);

  const sum = convertedPrices.reduce((total, price) => total + price, 0);
  return sum.toFixed(2);
};

// Calculate the sum of purchases made in the last X days
const purchasesLastXDaysAmount = (usersData, days) => {
  const now = Date.now();

  const prices = usersData?.flatMap(user =>
    (user.shop?.activity?.itemsAcquired || [])
      .filter(item => {
        const createdAtDate = getDateFromTimestamp(item.createdAt);
        return (now - createdAtDate.getTime()) <= days * 24 * 60 * 60 * 1000;
      })
      .map(item => item.price)
  ).filter(price => price !== undefined);

  const convertedPrices = convertPrices(prices);
  const sum = convertedPrices.reduce((total, price) => total + price, 0);

  return sum.toFixed(2);
};

// Convert prices to USD using defined currency rates and handle various formats
export const convertPrices = (prices) => {
  const parsePrice = (price) => {
    if (typeof price !== 'string') return 0;

    // Remove spaces and trim the input string
    price = price.replace(/\s/g, '').trim();

    // Replace commas with dots for decimal handling
    price = price.replace(',', '.');

    // Define the regex patterns for currency symbols and codes
    const currencyPattern = /[$€£₺złS\/R\$kr₩¥]|USD|EUR|TRY|PLN|KRW|COP|INR|GBP|US\$|¥|R\$/gi;

    // Identify if the price string includes a currency symbol or code and remove it
    let number = price.replace(currencyPattern, '');

    // Parse the cleaned string as a float number
    number = parseFloat(number);

    // Check if the price was recognized correctly
    if (isNaN(number)) {
      console.log('Failed to parse price:', price);
      return 0;
    }

    return number;
  };

  // Define the exchange rates, including Japanese Yen (JPY) and Brazilian Real (BRL)
  const exchangeRates = {
    EUR: { EUR: 1, USD: 1.119, GBP: 0.9334, CHF: 1.0226, PLN: 4.6897, TRY: 28, PEN: 4.4, INR: 91.5, KRW: 1310, JPY: 159.2, BRL: 5.3 },
    USD: { EUR: 0.8937, USD: 1, GBP: 0.7993, CHF: 0.9132, PLN: 4.0212, TRY: 34, PEN: 3.7, INR: 75, KRW: 1180, JPY: 142, BRL: 4.75, SEK: 8.8 },
    GBP: { EUR: 1.0716, USD: 1.251, GBP: 1, CHF: 1.1433, PLN: 5.0244, TRY: 32, PEN: 4.6, INR: 95, KRW: 1575, JPY: 179, BRL: 6.15 },
    CHF: { EUR: 0.9786, USD: 1.0956, GBP: 0.8745, CHF: 1, PLN: 4.4074, TRY: 29, PEN: 4.2, INR: 85, KRW: 1430, JPY: 160, BRL: 5.2 },
    PLN: { EUR: 0.2134, USD: 0.2486, GBP: 0.1991, CHF: 0.2269, PLN: 1, TRY: 6, PEN: 0.83, INR: 18.3, KRW: 290, JPY: 36, BRL: 1.3 },
    TRY: { EUR: 0.0357, USD: 0.02943, GBP: 0.0313, CHF: 0.0345, PLN: 0.1667, TRY: 1, PEN: 0.13, INR: 2.5, KRW: 40, JPY: 5, BRL: 0.2 },
    PEN: { EUR: 0.227, USD: 0.27, GBP: 0.217, CHF: 0.238, PLN: 1.2, TRY: 7.6, PEN: 1, INR: 20, KRW: 300, JPY: 40, BRL: 1.5 },
    INR: { EUR: 0.011, USD: 0.013, GBP: 0.0105, CHF: 0.0118, PLN: 0.055, TRY: 0.4, PEN: 0.05, INR: 1, KRW: 15.7, JPY: 1.9, BRL: 0.06 },
    KRW: { EUR: 0.00076, USD: 0.00085, GBP: 0.00065, CHF: 0.0007, PLN: 0.0034, TRY: 0.025, PEN: 0.0033, INR: 0.065, KRW: 1, JPY: 0.12, BRL: 0.004 },
    JPY: { EUR: 0.0063, USD: 0.007, GBP: 0.0056, CHF: 0.00625, PLN: 0.028, TRY: 0.2, PEN: 0.025, INR: 0.53, KRW: 8.4, JPY: 1, BRL: 0.034 },
    BRL: { EUR: 0.1887, USD: 0.21, GBP: 0.1625, CHF: 0.1923, PLN: 0.77, TRY: 5, PEN: 0.67, INR: 16.7, KRW: 252, JPY: 29, BRL: 1 },
    COP: { EUR: 0.00026, USD: 0.00029, GBP: 0.00023, CHF: 0.00026, PLN: 0.0012, TRY: 0.008, PEN: 0.001, INR: 0.02, KRW: 0.3, JPY: 0.04, BRL: 0.001 },
    SEK: { EUR: 0.094, USD: 0.11, GBP: 0.086, CHF: 0.097, PLN: 0.43, TRY: 3, PEN: 0.04, INR: 0.8, KRW: 12, JPY: 1.5, BRL: 0.05 },
  };

  const convertToCurrency = (price, fromCurrency, toCurrency) => {
    const rate = exchangeRates[fromCurrency][toCurrency];
    return parseFloat((price * rate).toFixed(2));
  };

  const cache = new Map();

  const convertedPrices = prices.map(price => {
    if (cache.has(price)) return cache.get(price); // Check if already cached

    if (typeof price === 'string' && price) {
      let fromCurrency = null;
      console.log('Price:', price);

      if (price.includes('€')) fromCurrency = 'EUR';
      else if (price.includes('$') || price.includes('USD') || price.includes('US$')) fromCurrency = 'USD';
      else if (price.includes('£')) fromCurrency = 'GBP';
      else if (price.includes('GBP')) fromCurrency = 'GBP';
      else if (price.includes('₺')) fromCurrency = 'TRY';
      else if (price.includes('zł')) fromCurrency = 'PLN';
      else if (price.includes('S/')) fromCurrency = 'PEN';
      else if (price.includes('R$')) fromCurrency = 'BRL'; // Recognize Brazilian Real
      else if (price.includes('INR')) fromCurrency = 'INR';
      else if (price.includes('₩')) fromCurrency = 'KRW';
      else if (price.includes('¥')) fromCurrency = 'JPY'; // Recognize Japanese Yen
      else if (price.includes('COP')) fromCurrency = 'COP'; // Recognize Colombian Peso
      else if (price.includes('R')) fromCurrency = 'BRL'; // Recognize Brazilian Real
      else if (price.includes('SEK')) fromCurrency = 'SEK'; // Recognize Swedish Krona
      else if (price.includes('kr')) fromCurrency = 'SEK'; // Recognize Swedish Krona

      if (fromCurrency) {
        let amount = parsePrice(price);
        let convertedAmount = convertToCurrency(amount, fromCurrency, 'USD');
        console.log('Converted:', convertedAmount, fromCurrency, amount);

        if (convertedAmount > 55 && convertedAmount < 110) {
          console.warn('Adjusted high value price:', price, 'Converted:', convertedAmount);
          convertedAmount /= 4;
        }

        if (convertedAmount > 110) {
          console.warn('Adjusted high value price:', price, 'Converted:', convertedAmount);
          convertedAmount /= 14;
        }

        cache.set(price, convertedAmount); // Cache the result
        return convertedAmount;
      } else {
        console.warn('Unrecognized currency format:', price);
        return 0;
      }
    } else if (typeof price === 'number') {
      return price;
    } else {
      console.log('Unrecognized price format:', price);
      return 0;
    }
  });

  return convertedPrices;
};

// Calculate average price and total cost of purchases
const calculatePurchasesStats = (usersData, totalPurchasers) => {
  const prices = usersData?.flatMap(user =>
    (user.shop?.activity?.itemsAcquired?.map(item => item.price) || [])
  ).filter(price => price !== undefined);

  const convertedPrices = convertPrices(prices);
  const averagePrice = parseFloat((convertedPrices.reduce((acc, curr) => acc + curr, 0) / convertedPrices.length).toFixed(2));

  const totalCost = parseFloat((averagePrice * totalPurchasers).toFixed(2));

  return { averagePrice, totalCost };
};

// Get accurate user count efficiently - now using direct Firebase count
const getRealUserCountFromStats = async () => {
  try {
    return await getRealUserCount();
  } catch (error) {
    console.warn('Failed to get real user count, falling back to getUsers method:', error);
    const { totalItems } = await getUsers({
      filters: {},
      pageSize: 1, // Just get one user to get the total count
    });
    return totalItems;
  }
};

// Optimized stats that doesn't load all users
export const getOptimizedStats = async () => {
  // First, get the real total count (this is fast)
  const realTotalUsers = await getRealUserCountFromStats();
  
  // Then use a sample for complex calculations
  const sampleSize = Math.min(1500, realTotalUsers); // Use larger sample but cap it
  const { data: usersData } = await getUsers({
    filters: {},
    pageSize: sampleSize,
  });

  console.log(`📊 Using sample of ${usersData.length} users from ${realTotalUsers} total users`);

  // Calculate scale factor from real data
  const sampleTotalUsers = usersData.length;
  const scaleFactor = realTotalUsers / sampleTotalUsers;

  // Calculate ratios from sample (these should be accurate)
  const sampleUsersWithCurrentReality = usersData.filter(user => user.currentReality).length;
  const sampleUserWithFreeDemoDone = usersData.filter(user => user.freeDemoCompleted);
  const sampleUsersWithFreeDemoCompleted = sampleUserWithFreeDemoDone.length;
  const sampleUsersWithRealitiesAcquired = usersData.filter(user => user.shop?.totalRealitiesAcquired > 0).length;

  // Recent data (absolute counts from sample - these are real numbers, not extrapolated)
  const newUsersLast24Hours = usersData.filter(user => isWithinTimeframe(user.createdAt, 24 * 3600)).length;
  const newUsersLast10Days = usersData.filter(user => isWithinTimeframe(user.createdAt, 10 * 24 * 3600)).length;
  const usersLast30Days = usersData.filter(user => isWithinTimeframe(user.createdAt, 30 * 24 * 3600));
  const newUsersLast30Days = usersLast30Days.length;
  
  // Get more comprehensive data for better calculations
  const sampleUsersWithAds = usersData.filter(user => user.adsConsented).length;
  const sampleTotalAdsSeen = usersData.reduce((acc, user) => acc + (user.totalAdsSeen || 0), 0);
  const sampleUsersWithMarketing = usersData.filter(user => user.emailMarketing).length;
  const sampleTotalReferrals = usersData.reduce((acc, user) => acc + (user.referral?.referredUsersTotal || 0), 0);

  // Purchaser data from sample
  const samplePurchasers = usersData.filter(user => user.shop?.totalRealitiesAcquired > 0);
  const sampleTotalPurchasers = samplePurchasers.length;
  const samplePurchasersWithFreeDemoCompleted = usersData.filter(user => user.shop?.totalRealitiesAcquired > 0 && user.freeDemoCompleted).length;

  // Calculate ratios (these are accurate regardless of sample size)
  const ratioUsersWithCurrentReality = formatRatio(sampleUsersWithCurrentReality / sampleTotalUsers);
  const ratioUsersWithFreeDemoCompleted = formatRatio(sampleUsersWithFreeDemoCompleted / sampleTotalUsers);
  const ratioPurchasers = formatRatio(sampleTotalPurchasers / sampleTotalUsers);
  const ratioFreeDemoCompletedPurchasers = sampleUsersWithFreeDemoCompleted > 0
    ? formatRatio(samplePurchasersWithFreeDemoCompleted / sampleUsersWithFreeDemoCompleted)
    : 0;

  // Extrapolated totals (estimates based on sample)
  const estimatedTotalUsersWithCurrentReality = Math.round(sampleUsersWithCurrentReality * scaleFactor);
  const estimatedTotalUsersWithFreeDemoCompleted = Math.round(sampleUsersWithFreeDemoCompleted * scaleFactor);
  const estimatedTotalPurchasers = Math.round(sampleTotalPurchasers * scaleFactor);

  // Language distribution from sample
  let ratioPerLanguage = {};
  usersData.forEach(user => {
    if (!ratioPerLanguage[user.language]) {
      ratioPerLanguage[user.language] = 1;
    } else {
      ratioPerLanguage[user.language]++;
    }
  });

  const ratioPerLanguageArray = Object.keys(ratioPerLanguage).map(language => {
    return {
      language,
      ratio: formatRatio(ratioPerLanguage[language] / sampleTotalUsers),
      users: Math.round(ratioPerLanguage[language] * scaleFactor) // Estimated total
    };
  });

  ratioPerLanguageArray.sort((a, b) => parseFloat(b.ratio) - parseFloat(a.ratio));

  // Gender distribution from sample
  const femaleUsers = usersData.filter(user => user.gender === 'female');
  const maleUsers = usersData.filter(user => user.gender === 'male');
  const femaleRatio = formatRatio(femaleUsers.length / sampleTotalUsers);
  const maleRatio = formatRatio(maleUsers.length / sampleTotalUsers);

  // Purchase calculations from sample
  const { averagePrice, totalCost } = calculatePurchasesStats(usersData, sampleTotalPurchasers);
  const estimatedTotalCost = totalCost * scaleFactor;

  // Purchase data with better calculations
  const sampleTotalPurchases = usersData.reduce((total, user) => total + (user.shop?.activity?.itemsAcquired?.length || 0), 0);
  const sampleFemalePurchasers = femaleUsers.filter(user => user.shop?.totalRealitiesAcquired > 0).length;
  const sampleMalePurchasers = maleUsers.filter(user => user.shop?.totalRealitiesAcquired > 0).length;
  const sampleNonPurchasers = sampleTotalUsers - sampleTotalPurchasers;
  const sampleUsersWithFreeDemoLast10Days = sampleUserWithFreeDemoDone.filter(user => 
    isWithinTimeframe(user.createdAt, 10 * 24 * 3600)).length;
  const sampleUsersWithFreeDemoLast30Days = usersLast30Days.filter(user => user.freeDemoCompleted).length;
  
  // Recent purchase activity (absolute counts from sample)
  const purchasesLast24Hours = usersData.filter(user => user.shop?.activity?.itemsAcquired?.some(item => {
    return isWithinTimeframe(item.createdAt, 24 * 3600);
  })).length;
  const purchasesLast10Days = usersData.filter(user => user.shop?.activity?.itemsAcquired?.some(item => 
    isWithinTimeframe(item.createdAt, 10 * 24 * 3600))).length;
  const purchasesLast30Days = usersData.filter(user => user.shop?.activity?.itemsAcquired?.some(item => 
    isWithinTimeframe(item.createdAt, 30 * 24 * 3600))).length;

  // Calculate purchase amounts from sample
  const amountPurchasesLast24Hours = purchasesLast24HoursAmount(usersData);
  const amountPurchasesLast10Days = purchasesLastXDaysAmount(usersData, 10);
  const amountPurchasesLast30Days = purchasesLastXDaysAmount(usersData, 30);

  return {
    // Meta info
    isOptimized: true,
    sampleSize: sampleTotalUsers,
    estimatedTotal: realTotalUsers,
    scaleFactor: scaleFactor.toFixed(2),

    // Core metrics (using real total + extrapolated estimates)
    totalUsers: realTotalUsers,
    totalUsersWithCurrentReality: estimatedTotalUsersWithCurrentReality,
    totalUsersWithFreeDemoCompleted: estimatedTotalUsersWithFreeDemoCompleted,
    totalPurchasers: estimatedTotalPurchasers,
    totalNonPurchasers: Math.round(sampleNonPurchasers * scaleFactor),
    totalPurchases: Math.round(sampleTotalPurchases * scaleFactor),
    totalUsersWithAds: Math.round(sampleUsersWithAds * scaleFactor),
    totalAdsSeen: Math.round(sampleTotalAdsSeen * scaleFactor),
    totalUsersWithMarketingConsented: Math.round(sampleUsersWithMarketing * scaleFactor),
    totalReferrals: Math.round(sampleTotalReferrals * scaleFactor),
    totalUsersWithFreeDemoCompletedLast10Days: Math.round(sampleUsersWithFreeDemoLast10Days * scaleFactor),
    totalUsersWithFreeDemoCompletedLast30Days: Math.round(sampleUsersWithFreeDemoLast30Days * scaleFactor),
    totlalUsersWithRealityLast30Days: usersLast30Days.filter(user => user.currentReality).length,

    // Ratios (accurate from sample)
    ratioUsersWithCurrentReality,
    ratioUsersWithFreeDemoCompleted,
    ratioPurchasers,
    ratioFreeDemoCompletedPurchasers,
    ratioNonPurchasers: formatRatio(sampleNonPurchasers / sampleTotalUsers),
    ratioPurchasesByPurchasers: sampleTotalPurchasers > 0 ? formatRatio(sampleTotalPurchases / sampleTotalPurchasers, false) : 0,
    ratioTotalUsersWithAds: formatRatio(sampleUsersWithAds / sampleTotalUsers),
    ratioTotalUsersWithMarketingConsented: formatRatio(sampleUsersWithMarketing / sampleTotalUsers),
    ratioUsersWithFreeDemoCompletedLast10Days: formatRatio(sampleUsersWithFreeDemoLast10Days / realTotalUsers),
    ratioTotalUsersWithFreeDemoCompletedLast30Days: formatRatio(sampleUsersWithFreeDemoLast30Days / newUsersLast30Days || 0),
    ratioTotalUsersWithRealityLast30Days: formatRatio((usersLast30Days.filter(user => user.currentReality).length) / newUsersLast30Days || 0),

    // Recent activity (absolute counts - NOT scaled)
    newUsersLast24Hours,
    newUsersLast10Days,
    newUsersLast30Days,
    ratioNewUsersLast24Hours: formatRatio(newUsersLast24Hours / realTotalUsers),
    ratioNewUsersLast10Days: formatRatio(newUsersLast10Days / realTotalUsers),
    ratioNewUsersLast30Days: formatRatio(newUsersLast30Days / realTotalUsers),
    
    // Missing ratios from the frontend
    ratioUsersWithRealityCreated: formatRatio(sampleUsersWithCurrentReality / sampleTotalUsers),
    ratioOfRealitiesAcquired: formatRatio((usersData.reduce((acc, user) => acc + (user.shop?.totalRealitiesAcquired || 0), 0)) / sampleTotalUsers),
    ratioPurchaseOf3Realities: sampleTotalPurchasers > 0 ? formatRatio(usersData.filter(user => (user.shop?.totalRealitiesAcquired || 0) >= 3).length / sampleTotalPurchasers) : 0,

    // Purchase activity (absolute counts from sample)
    purchasesLast24Hours,
    purchasesLast10Days,
    purchasesLast30Days,
    amountPurchasesLast24Hours,
    amountPurchasesLast10Days,
    amountPurchasesLast30Days,

    // Distribution data
    ratioPerLanguage: ratioPerLanguageArray,
    femaleUsers: { length: Math.round(femaleUsers.length * scaleFactor) },
    maleUsers: { length: Math.round(maleUsers.length * scaleFactor) },
    femaleRatio,
    maleRatio,
    femalePurchasers: Math.round(sampleFemalePurchasers * scaleFactor),
    malePurchasers: Math.round(sampleMalePurchasers * scaleFactor),
    ratioFemalePurchase: sampleTotalPurchasers > 0 ? formatRatio(sampleFemalePurchasers / sampleTotalPurchasers) : 0,
    ratioMalePurchase: sampleTotalPurchasers > 0 ? formatRatio(sampleMalePurchasers / sampleTotalPurchasers) : 0,

    // Financial data
    averagePrice,
    totalCost: estimatedTotalCost,
    avgAdsSeenPerUser: sampleUsersWithAds > 0 ? Math.round(sampleTotalAdsSeen / sampleUsersWithAds) : 0,

    // Language-based purchase data
    purchasesPerLanguage: (() => {
      let purchasesPerLanguage = {};
      usersData.forEach(user => {
        if ((user.shop?.totalRealitiesAcquired || 0) > 0) {
          const lang = user.language || 'unknown';
          if (!purchasesPerLanguage[lang]) {
            purchasesPerLanguage[lang] = user.shop?.activity?.itemsAcquired?.length || 0;
          } else {
            purchasesPerLanguage[lang] += user.shop?.activity?.itemsAcquired?.length || 0;
          }
        }
      });
      return purchasesPerLanguage;
    })(),

    ratioPurchasesPerLanguage: (() => {
      let purchasesPerLanguage = {};
      let ratioPurchasesPerLanguage = {};
      usersData.forEach(user => {
        if ((user.shop?.totalRealitiesAcquired || 0) > 0) {
          const lang = user.language || 'unknown';
          if (!purchasesPerLanguage[lang]) {
            purchasesPerLanguage[lang] = user.shop?.activity?.itemsAcquired?.length || 0;
          } else {
            purchasesPerLanguage[lang] += user.shop?.activity?.itemsAcquired?.length || 0;
          }
        }
      });
      const totalSamplePurchases = Object.values(purchasesPerLanguage).reduce((sum, count) => sum + count, 0);
      Object.keys(purchasesPerLanguage).forEach(language => {
        ratioPurchasesPerLanguage[language] = totalSamplePurchases > 0 ? 
          formatRatio(purchasesPerLanguage[language] / totalSamplePurchases) : 0;
      });
      return ratioPurchasesPerLanguage;
    })(),

    // Advanced metrics
    purchasersWithFreeDemoCompleted: Math.round(samplePurchasersWithFreeDemoCompleted * scaleFactor),
    totalRealitiesAcquired: Math.round(usersData.reduce((acc, user) => acc + (user.shop?.totalRealitiesAcquired || 0), 0) * scaleFactor),
    ratioOfRealitiesAcquired: formatRatio((usersData.reduce((acc, user) => acc + (user.shop?.totalRealitiesAcquired || 0), 0)) / sampleTotalUsers),
    totalPurchasersOf3Realities: Math.round(usersData.filter(user => (user.shop?.totalRealitiesAcquired || 0) >= 3).length * scaleFactor),
    ratioPurchaseOf3Realities: sampleTotalPurchasers > 0 ? formatRatio(usersData.filter(user => (user.shop?.totalRealitiesAcquired || 0) >= 3).length / sampleTotalPurchasers) : 0,

    // Expected income calculations
    expectedPurchasesNext10Days: sampleUsersWithFreeDemoLast10Days > 0 && sampleUsersWithFreeDemoCompleted > 0 ? 
      Math.round(sampleUsersWithFreeDemoLast10Days * (samplePurchasersWithFreeDemoCompleted / sampleUsersWithFreeDemoCompleted) * scaleFactor) : 0,
    expectedIncomeNext10Days: sampleUsersWithFreeDemoLast10Days > 0 && sampleUsersWithFreeDemoCompleted > 0 ? 
      Math.round(sampleUsersWithFreeDemoLast10Days * (samplePurchasersWithFreeDemoCompleted / sampleUsersWithFreeDemoCompleted) * scaleFactor * averagePrice) : 0,
    expectedPurchasesMonthly: sampleUsersWithFreeDemoCompleted > 0 ? 
      Math.round(sampleUsersWithFreeDemoCompleted * (samplePurchasersWithFreeDemoCompleted / sampleUsersWithFreeDemoCompleted) * scaleFactor) : 0,
    expectedIncomeMonthly: sampleUsersWithFreeDemoCompleted > 0 ? 
      Math.round(sampleUsersWithFreeDemoCompleted * (samplePurchasersWithFreeDemoCompleted / sampleUsersWithFreeDemoCompleted) * scaleFactor * averagePrice) : 0,

    // Calculate average time from signup to purchase (simplified for sample)
    avgTimeFromSignupToPurchase: sampleTotalPurchasers > 0 ? (() => {
      const recentPurchasers = samplePurchasers.slice(-Math.min(20, samplePurchasers.length));
      const totalMinutes = recentPurchasers.reduce((acc, user) => {
        const signupTimestamp = user.createdAt;
        const firstPurchase = user.shop?.activity?.itemsAcquired?.[0];
        if (!firstPurchase) return acc;
        
        const signupDate = new Date(signupTimestamp?.seconds * 1000 + (signupTimestamp?.nanoseconds || 0) / 1000000);
        const purchaseDate = new Date(firstPurchase.createdAt?.seconds * 1000 + (firstPurchase.createdAt?.nanoseconds || 0) / 1000000);
        
        if (isNaN(signupDate) || isNaN(purchaseDate)) return acc;
        
        const timeFromSignupToPurchaseInHours = (purchaseDate - signupDate) / (1000 * 60 * 60);
        return acc + timeFromSignupToPurchaseInHours;
      }, 0);
      return recentPurchasers.length > 0 ? (totalMinutes / recentPurchasers.length).toFixed(2) : 0;
    })() : 0,

    // Note about optimization
    optimizationNote: `📊 Stats calculated from ${sampleTotalUsers} user sample (${(sampleTotalUsers/realTotalUsers*100).toFixed(1)}% of total)`
  };
};

// Keep the original function but add a limit parameter to make it safer
export const getStats = async (useOptimized = true) => {
  if (useOptimized) {
    return getOptimizedStats();
  }

  // Original function with safety limit
  const limit = 2000; // Reduced from 10000 to avoid quotas
  console.warn(`⚠️ Using non-optimized stats with limit of ${limit} users. Consider using optimized version.`);

  const { data: usersData } = await getUsers({
    filters: {},
    pageSize: limit
  });

  const totalUsers = usersData.length;
  const totalUsersWithCurrentReality = usersData.filter(user => user.currentReality).length;
  const userWithFreeDemoDone = usersData.filter(user => user.freeDemoCompleted);
  const totalUsersWithFreeDemoCompleted = userWithFreeDemoDone.length;
  const totalUsersWithFreeDemoCompletedLast10Days = userWithFreeDemoDone.filter(user => isWithinTimeframe(user.createdAt, 10 * 24 * 3600)).length;
  const totalUsersWithRealitiesAcquired = usersData.filter(user => user.shop?.totalRealitiesAcquired > 0).length;
  const purchasersWithFreeDemoCompleted = usersData.filter(user => user.shop?.totalRealitiesAcquired > 0 && user.freeDemoCompleted).length;
  const ratioFreeDemoCompletedPurchasers = formatRatio(purchasersWithFreeDemoCompleted / totalUsersWithFreeDemoCompleted);

  const newUsersLast24Hours = usersData.filter(user => isWithinTimeframe(user.createdAt, 24 * 3600)).length;
  const newUsersLast10Days = usersData.filter(user => isWithinTimeframe(user.createdAt, 10 * 24 * 3600)).length;
  const usersLast30Days = usersData.filter(user => isWithinTimeframe(user.createdAt, 30 * 24 * 3600));
  const newUsersLast30Days = usersLast30Days.length;
  const purchasers = usersData.filter(user => user.shop?.totalRealitiesAcquired > 0);

  const totlalUsersWithRealityLast30Days = usersLast30Days.filter(user => user.currentReality).length;
  const totalUsersWithFreeDemoCompletedLast30Days = usersLast30Days.filter(user => user.freeDemoCompleted).length;
  const ratioTotalUsersWithFreeDemoCompletedLast30Days = formatRatio(totalUsersWithFreeDemoCompletedLast30Days / newUsersLast30Days);
  const ratioTotalUsersWithRealityLast30Days = formatRatio(totlalUsersWithRealityLast30Days / newUsersLast30Days);

  const totalPurchasers = purchasers.length;
  const totalPurchases = usersData.reduce((total, user) => total + (user.shop?.activity?.itemsAcquired?.length || 0), 0);
  const totalNonPurchasers = totalUsers - totalPurchasers;
  const totalPurchasersOf3Realities = usersData.filter(user => user.shop?.totalRealitiesAcquired >= 3).length;
  const totalRealitiesAcquired = usersData.reduce((acc, user) => acc + (user.shop?.totalRealitiesAcquired || 0), 0);

  const purchasesLast24Hours = usersData.filter(user => user.shop?.activity?.itemsAcquired?.some(item => {
    return isWithinTimeframe(item.createdAt, 24 * 3600);
  })).length;
  const purchasesLast10Days = usersData.filter(user => user.shop?.activity?.itemsAcquired?.some(item => isWithinTimeframe(item.createdAt, 10 * 24 * 3600))).length;
  const purchasesLast30Days = usersData.filter(user => user.shop?.activity?.itemsAcquired?.some(item => isWithinTimeframe(item.createdAt, 30 * 24 * 3600))).length;

  const amountPurchasesLast24Hours = purchasesLast24HoursAmount(usersData);
  const amountPurchasesLast10Days = purchasesLastXDaysAmount(usersData, 10);
  const amountPurchasesLast30Days = purchasesLastXDaysAmount(usersData, 30);

  const ratioPurchasers = formatRatio(totalPurchasers / totalUsers);
  const ratioPurchasesByPurchasers = formatRatio(totalPurchases / totalPurchasers, false);
  const ratioNonPurchasers = formatRatio(totalNonPurchasers / totalUsers);
  const ratioPurchaseOf3Realities = formatRatio(totalPurchasersOf3Realities / totalPurchasers);
  const ratioUsersWithRealityCreated = formatRatio(totalUsersWithCurrentReality / totalUsers);
  const ratioUsersWithFreeDemoCompleted = formatRatio(totalUsersWithFreeDemoCompleted / totalUsers);
  const ratioUsersWithFreeDemoCompletedLast10Days = formatRatio(totalUsersWithFreeDemoCompletedLast10Days / totalUsers);
  const ratioNewUsersLast24Hours = formatRatio(newUsersLast24Hours / totalUsers);
  const ratioNewUsersLast10Days = formatRatio(newUsersLast10Days / totalUsers);
  const ratioNewUsersLast30Days = formatRatio(newUsersLast30Days / totalUsers);
  const ratioOfPurchaseOf3Realities = formatRatio(totalPurchasersOf3Realities / totalPurchasers);
  const ratioOfRealitiesAcquired = formatRatio(totalRealitiesAcquired / totalUsers);
  const ratioPurchasesLast10Days = formatRatio(purchasesLast10Days / totalUsers);

  const firstAdsSeenDate = new Date('2024-08-01');
  const filteredUsersData = usersData.filter(user => {
    const userCreationDate = getDateFromTimestamp(user.createdAt);
    return userCreationDate >= firstAdsSeenDate;
  });

  const totalUsersFiltered = filteredUsersData.length;
  const totalUsersWithAds = usersData.filter(user => user.adsConsented).length;
  const ratioTotalUsersWithAds = formatRatio(totalUsersWithAds / totalUsersFiltered);
  const totalAdsSeen = usersData.reduce((acc, user) => acc + (user.totalAdsSeen || 0), 0);
  const ratioAdSeenPerUser = formatRatio(totalAdsSeen / totalUsers);
  const avgAdsSeenPerUser = (totalAdsSeen / totalUsersWithAds).toFixed(0);
  const totalUsersWithMarketingConsented = usersData.filter(user => user.emailMarketing).length;
  const ratioUsersWithMarketingConsented = formatRatio(totalUsersWithMarketingConsented / totalUsersFiltered);

  const { averagePrice, totalCost } = calculatePurchasesStats(usersData, totalPurchasers);
  const lastTenPurchasers = purchasers.slice(-20);

  const totalMinutesFromSignupToPurchase = lastTenPurchasers.reduce((acc, user) => {
    const signupTimestamp = user.createdAt;
    const purchaseTimestamp = user.shop?.activity?.itemsAcquired?.[0]?.createdAt;

    if (!signupTimestamp || !purchaseTimestamp) {
      return acc;
    }

    const signupDate = new Date(signupTimestamp?.seconds * 1000 + (signupTimestamp?.nanoseconds || 0) / 1000000);
    const purchaseDate = new Date(purchaseTimestamp?.seconds * 1000 + (purchaseTimestamp?.nanoseconds || 0) / 1000000);

    if (isNaN(signupDate) || isNaN(purchaseDate)) {
      return acc;
    }

    const timeFromSignupToPurchase = purchaseDate - signupDate;
    const timeFromSignupToPurchaseInHours = timeFromSignupToPurchase / 1000 / 60 / 60;

    return acc + timeFromSignupToPurchaseInHours;
  }, 0);

  const avgTimeFromSignupToPurchase = totalPurchasers > 0
    ? totalMinutesFromSignupToPurchase / totalPurchasers
    : 0;

  let ratioPerLanguage = {};

  usersData.forEach(user => {
    if (!ratioPerLanguage[user.language]) {
      ratioPerLanguage[user.language] = 1;
    } else {
      ratioPerLanguage[user.language]++;
    }
  });

  const ratioPerLanguageArray = Object.keys(ratioPerLanguage).map(language => {
    return {
      language,
      ratio: formatRatio(ratioPerLanguage[language] / totalUsers),
      users: ratioPerLanguage[language]
    };
  });

  ratioPerLanguageArray.sort((a, b) => parseFloat(b.ratio) - parseFloat(a.ratio));

  let purchasesPerLanguage = {};

  usersData.forEach(user => {
    if (user.shop?.totalRealitiesAcquired > 0) {
      if (!purchasesPerLanguage[user.language]) {
        purchasesPerLanguage[user.language] = user.shop?.activity?.itemsAcquired?.length || 0;
      } else {
        purchasesPerLanguage[user.language] += user.shop?.activity?.itemsAcquired?.length || 0;
      }
    }
  });

  let ratioPurchasesPerLanguage = {};

  Object.keys(purchasesPerLanguage).forEach(language => {
    ratioPurchasesPerLanguage[language] = formatRatio(purchasesPerLanguage[language] / totalPurchases);
  });

  const ratioFreeDemoCompletedPurchasersDecimal = parseFloat(ratioFreeDemoCompletedPurchasers) / 100;
  const expectedPurchasesNext10Days = (totalUsersWithFreeDemoCompletedLast10Days * ratioFreeDemoCompletedPurchasersDecimal).toFixed(2);
  const expectedIncomeNext10Days = (expectedPurchasesNext10Days * averagePrice).toFixed(2);
  const expectedPurchasesMonthly = (totalUsersWithFreeDemoCompleted * ratioFreeDemoCompletedPurchasersDecimal).toFixed(2);
  const expectedIncomeMonthly = (expectedPurchasesMonthly * averagePrice).toFixed(2);

  const femaleUsers = usersData.filter(user => user.gender === 'female');
  const maleUsers = usersData.filter(user => user.gender === 'male');
  const femaleRatio = formatRatio(femaleUsers.length / totalUsers);
  const maleRatio = formatRatio(maleUsers.length / totalUsers);
  const femalePurchasers = femaleUsers.filter(user => user.shop?.totalRealitiesAcquired > 0).length;
  const ratioFemalePurchase = formatRatio(femalePurchasers / totalPurchasers);
  const malePurchasers = maleUsers.filter(user => user.shop?.totalRealitiesAcquired > 0).length;
  const ratioMalePurchase = formatRatio(malePurchasers / totalPurchasers);
  const totalReferrals = usersData.reduce((acc, user) => acc + (user.referral?.referredUsersTotal || 0), 0);

  return {
    totalUsers,
    totalUsersWithCurrentReality,
    totlalUsersWithRealityLast30Days,
    ratioTotalUsersWithRealityLast30Days,
    totalUsersWithFreeDemoCompletedLast30Days,
    ratioTotalUsersWithFreeDemoCompletedLast30Days,
    totalUsersWithFreeDemoCompleted,
    totalUsersWithFreeDemoCompletedLast10Days,
    totalUsersWithRealitiesAcquired,
    totalPurchasers,
    totalNonPurchasers,
    totalPurchasersOf3Realities,
    totalRealitiesAcquired,
    ratioPurchasers,
    ratioNonPurchasers,
    ratioPurchaseOf3Realities,
    ratioPerLanguage: ratioPerLanguageArray,
    ratioUsersWithRealityCreated,
    ratioUsersWithFreeDemoCompleted,
    ratioUsersWithFreeDemoCompletedLast10Days,
    ratioNewUsersLast24Hours,
    ratioNewUsersLast10Days,
    ratioNewUsersLast30Days,
    newUsersLast24Hours,
    newUsersLast10Days,
    newUsersLast30Days,
    ratioOfPurchaseOf3Realities,
    ratioOfRealitiesAcquired,
    averagePrice,
    totalCost,
    purchasesLast10Days,
    ratioPurchasesLast10Days,
    amountPurchasesLast10Days,
    purchasesPerLanguage,
    ratioPurchasesPerLanguage,
    purchasersWithFreeDemoCompleted,
    ratioFreeDemoCompletedPurchasers,
    expectedPurchasesNext10Days,
    expectedIncomeNext10Days,
    expectedPurchasesMonthly,
    expectedIncomeMonthly,
    femaleUsers,
    maleUsers,
    femaleRatio,
    maleRatio,
    femalePurchasers,
    ratioFemalePurchase,
    malePurchasers,
    ratioMalePurchase,
    totalPurchases,
    ratioPurchasesByPurchasers,
    purchasesLast24Hours,
    amountPurchasesLast24Hours,
    purchasesLast30Days,
    amountPurchasesLast30Days,
    totalUsersWithAds,
    totalAdsSeen,
    totalReferrals,
    ratioTotalUsersWithAds: ratioTotalUsersWithAds,
    ratioAdSeenPerUser,
    avgAdsSeenPerUser,
    avgTimeFromSignupToPurchase,
    totalUsersWithMarketingConsented,
    ratioUsersWithMarketingConsented,
  };
};
